import type { NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'
import { useFetchAllProductReviews } from '@@partner:graphql/query/product/productReviews/allProductReviews.gql'
import { generatePath } from 'react-router-dom'

import { FlexboxGrid, Pagination, SelectPicker } from 'rsuite'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import ProductReviewTable from './grid/ProductReviewTable'
import ProductReviewFilter from './grid/ProductReviewFilter'

import { ROUTE_CATALOG_PRODUCT_REVIEW_EDIT } from '@@partner:config/routes'
import { useGridHelper } from '@@partner:helpers/crud/grid'

export interface ProductReviewsGridProps {
  brandSlug: string
  filter: {}
  showIdsFilters?: boolean
}

export default function ProductReviewsGrid ({
  brandSlug,
  filter,
  showIdsFilters
}: ProductReviewsGridProps) {
  const [ filterState, setFilterState ] = useState<NestedFilter>(filter)

  const {
    page,
    limit,
    defaultLimitOptions,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleSortColumn
  } = useGridHelper()

  const { data, loading, error } = useFetchAllProductReviews({
    pager: {
      page,
      limit
    },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    },
    filter: filterState
  })

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      // Объединяем внешний фильтр (filter из props) с фильтром из UI
      if (filter && Object.keys(filter).length > 0) {
        setFilterState({
          and: [ filter, updatedFilter ]
        })
      } 
      else {
        setFilterState(updatedFilter)
      }
      handlePageSelect(1)
    },
    [ filter, handlePageSelect ]
  )

  const generateEditUrl = useCallback(
    (productReviewId) => {
      if (!brandSlug) {
        return ''
      }
      return generatePath(ROUTE_CATALOG_PRODUCT_REVIEW_EDIT, {
        brandSlug,
        productReviewId
      })
    },
    [ brandSlug ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки товаров"
        error={error}
      />
    )
  }

  const productReviews = data?.productReviews.edges ?? []
  const pageInfo = data?.productReviews.pageInfo

  return (
    <div>
      <ProductReviewFilter
        showIdsFilters={showIdsFilters}
        onFilterChanged={handleFilterChanged}
      />

      <ProductReviewTable
        tableSort={tableSort}
        loading={loading}
        productReviews={productReviews}
        generateEditUrl={generateEditUrl}
        onSortColumn={handleSortColumn}
      />

      <FlexboxGrid
        justify="space-between"
        align="middle"
        style={{ marginTop: 10 }}
      >
        <Pagination
          last
          first
          next
          prev
          ellipsis
          activePage={page}
          pages={pageInfo?.lastPage}
          maxButtons={10}
          total={pageInfo?.total ?? (pageInfo?.lastPage ?? 0) * limit}
          onSelect={handlePageSelect}
        />
        <SelectPicker
          value={limit}
          data={defaultLimitOptions.map((key) => ({ value: key, label: key }))}
          cleanable={false}
          searchable={false}
          onChange={handleLimitSelect}
        />
      </FlexboxGrid>
    </div>
  )
}
